import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * CancelUndo component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCancelUndo extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.15851 1.12755C5.56035 1.22801 5.80468 1.63521 5.70421 2.03706L5.45299 3.04196C7.29646 1.76903 9.58605 1.10938 11.9476 1.10938C14.9486 1.10938 17.666 2.32368 19.6352 4.28774C19.6489 4.29963 19.6623 4.31209 19.6754 4.32514C19.6898 4.33963 19.7036 4.35458 19.7167 4.36992C21.6447 6.33396 22.8337 9.02586 22.8337 11.9955C22.8337 18.0077 17.9599 22.8816 11.9476 22.8816C5.9354 22.8816 1.06152 18.0077 1.06152 11.9955C1.06152 11.5813 1.39731 11.2455 1.81152 11.2455C2.22574 11.2455 2.56152 11.5813 2.56152 11.9955C2.56152 14.332 3.41529 16.4692 4.82788 18.112L18.0641 4.87573C16.4213 3.46314 14.2842 2.60938 11.9476 2.60938C9.64332 2.60938 7.47644 3.31814 5.84739 4.6164L7.49966 5.02947C7.90151 5.12993 8.14583 5.53713 8.04537 5.93898C7.9449 6.34083 7.5377 6.58515 7.13586 6.48469L4.01432 5.7043C3.61247 5.60384 3.36815 5.19664 3.46862 4.79479L4.249 1.67326C4.34946 1.27141 4.75666 1.02709 5.15851 1.12755ZM19.1202 5.94096L5.89312 19.168C7.52751 20.5491 9.64035 21.3816 11.9476 21.3816C17.1314 21.3816 21.3337 17.1793 21.3337 11.9955C21.3337 9.6882 20.5012 7.57535 19.1202 5.94096Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-cancel-undo') === undefined) {
  customElements.define('cat-icon-cancel-undo', BlocksIconCancelUndo);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-cancel-undo': BlocksIconCancelUndo;
  }
}
